import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Link } from "react-router-dom";
import CouponsManager from "../components/CouponsManager";
import SellerKycWizard from "../components/SellerKycWizard";
import { AddressFields, PhoneField, IdProofField } from "../components/ProfileFields";
import SellerPaymentsTab from "../components/SellerPaymentsTab";
import PlansTab from "../components/PlansTab";
import VoiceMicButton from "../components/VoiceMicButton";
import SellerDashboard, { Stat } from "../components/seller/SellerDashboard";
import SellerOrdersTab, {
  ShippingCountriesCard,
} from "../components/seller/SellerOrdersTab";
import SellerInboxTab from "../components/seller/SellerInboxTab";
import SellerRentalsTab from "../components/seller/SellerRentalsTab";
import WholesaleSlabsEditor from "../components/WholesaleSlabsEditor";
import ColorChipEditor from "../components/ColorChipEditor";
import AddProductModal from "../components/seller/AddProductModal";
import ShareStoreModal from "../components/ShareStoreModal";
import SellerQuickLinks from "../components/seller/SellerQuickLinks";
import SellerCurrencyPicker from "../components/seller/SellerCurrencyPicker";
import { SellerCurrencyProvider, useSellerCurrency } from "../lib/sellerCurrency";
import {
  Package, PackageX, PackageCheck, Store, TrendingUp, DollarSign, Users, Plus, Radio,
  Search as SearchIcon, Trash2, Pencil, Copy, Power, PowerOff, Share2,
  Upload, Save, X, Check, Lock, User as UserIcon, Image as ImageIcon, Video as VideoIcon, FileSpreadsheet,
  Clock,
} from "lucide-react";

const TABS = [
  ["dashboard", "Dashboard"],
  ["manage", "Products"],
  ["orders", "Orders"],
  ["stores", "Stores"],
  ["coupons", "Coupons"],
  ["rentals", "Rent Products"],
  ["payments", "Payments"],
  ["plans", "Plans"],
  ["inbox", "Inbox"],
  ["kyc", "Verification"],
  ["profile", "Profile"],
  ["bulk", "Bulk add"],
];

// CSV header keys we accept (lower-cased)
const CSV_FIELDS = ["name", "description", "price", "category_id", "category_name", "product_type_id", "product_type_name", "store_id", "store_name", "sizes", "stock", "image", "video_url", "banner_url", "gender", "delivery_days", "pincodes"];

function parseCsv(text) {
  // Minimal CSV parser supporting quoted values & commas inside quotes
  const rows = [];
  let row = []; let val = ""; let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { val += '"'; i++; }
      else if (ch === '"') inQ = false;
      else val += ch;
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") { row.push(val); val = ""; }
      else if (ch === "\n") { row.push(val); rows.push(row); row = []; val = ""; }
      else if (ch === "\r") { /* skip */ }
      else val += ch;
    }
  }
  if (val.length || row.length) { row.push(val); rows.push(row); }
  return rows.filter(r => r.some(c => (c || "").trim().length));
}

function emptyDraft() {
  return {
    // Stable per-row uid so React's reconciliation doesn't lose focus
    // when rows are removed from the middle of the bulk-add table.
    _uid: `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: "", description: "", price: "", category_id: "", product_type_id: "", store_id: "",
    sizes: "", stock: "10", image: "", video_url: "", banner_url: "",
    gender: "unisex", delivery_days: "5",
    is_new_launch: false, launch_date: "",
    is_advance_booking: false,
    advance_booking_opens_at: "", advance_booking_closes_at: "",
    expected_ship_date: "",
  };
}

export default function SellerPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [subscription, setSubscription] = useState(null);

  // Fetch subscription status
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/seller/subscription");
        setSubscription(data);
      } catch (e) {
        // Non-fatal: seller may not have a subscription yet.
        if (process.env.NODE_ENV !== "production") console.warn("[seller] subscription fetch:", e?.message);
      }
    })();
  }, []);

  // Deep-link via hash: /seller#inbox or /seller#rentals
  useEffect(() => {
    const h = (window.location.hash || "").replace(/^#/, "");
    if (h.startsWith("inbox")) setTab("inbox");
    if (h.startsWith("rentals")) setTab("rentals");
  }, []);

  const [analytics, setAnalytics] = useState(null);
  const [stores, setStores] = useState([]);
  const [shareStore, setShareStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [profile, setProfile] = useState(null);
  const [storeForm, setStoreForm] = useState({ name: "", description: "", category_id: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [productsTable, setProductsTable] = useState([]);
  const [tableSearch, setTableSearch] = useState("");
  const [tableStoreFilter, setTableStoreFilter] = useState("");
  const [tableCatFilter, setTableCatFilter] = useState("");
  const [tableSort, setTableSort] = useState("");
  const [editing, setEditing] = useState(null); // product being edited inline
  const [drafts, setDrafts] = useState([emptyDraft()]); // multi-row bulk draft
  const csvInputRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [orderStatusFilter, setOrderStatusFilter] = useState("");
  const [orderReturnFilter, setOrderReturnFilter] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);

  const flash = useCallback((m, isErr = false) => {
    if (isErr) { setErr(m); setMsg(""); }
    else { setMsg(m); setErr(""); }
    setTimeout(() => { setMsg(""); setErr(""); }, 2800);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [a, s, c, pt] = await Promise.all([
        api.get("/seller/analytics"),
        api.get("/seller/stores"),
        api.get("/categories"),
        api.get("/product-types"),
      ]);
      setAnalytics(a.data); setStores(s.data); setCategories(c.data);
      setProductTypes(pt.data || []);
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Load failed", true); }
  }, [flash]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const loadProductsTable = useCallback(async () => {
    const params = new URLSearchParams();
    if (tableSearch) params.set("search", tableSearch);
    if (tableStoreFilter) params.set("store_id", tableStoreFilter);
    if (tableCatFilter) params.set("category_id", tableCatFilter);
    if (tableSort) params.set("sort", tableSort);
    const { data } = await api.get(`/seller/products?${params}`);
    setProductsTable(data);
  }, [tableSearch, tableStoreFilter, tableCatFilter, tableSort]);
  useEffect(() => {
    if (tab === "manage") loadProductsTable();
    if (tab === "coupons" && productsTable.length === 0) loadProductsTable();
  }, [tab, loadProductsTable, productsTable.length]);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/seller/profile");
      setProfile(data);
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Profile load failed", true); }
  }, [flash]);
  useEffect(() => { if (tab === "profile") loadProfile(); }, [tab, loadProfile]);

  const loadOrders = useCallback(async () => {
    const params = new URLSearchParams();
    if (orderStatusFilter) params.set("status", orderStatusFilter);
    if (orderReturnFilter) params.set("return_status", orderReturnFilter);
    try {
      const { data } = await api.get(`/seller/orders?${params}`);
      setOrders(data);
    } catch (e) { flash(formatApiError(e?.response?.data?.detail) || "Orders load failed", true); }
  }, [orderStatusFilter, orderReturnFilter, flash]);
  useEffect(() => {
    if (tab === "orders") loadOrders();
  }, [tab, loadOrders]);

  const updateOrderStatus = async (orderId, status, note = "") => {
    try {
      await api.post(`/seller/orders/${orderId}/status`, { status, note });
      flash(`Marked ${status.replace("_", " ")}.`);
      loadOrders();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };
  const confirmPayment = async (orderId) => {
    try {
      await api.post(`/seller/orders/${orderId}/payment/confirm`, { note: "" });
      flash("Payment confirmed — order moved to processing.");
      loadOrders();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };
  const decideReturn = async (orderId, decision, note = "") => {
    try {
      await api.post(`/seller/orders/${orderId}/return/decision`, { decision, note });
      flash(`Return ${decision === "approve" ? "approved" : "rejected"}.`);
      loadOrders();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };
  const markReturnPickup = async (orderId) => {
    try {
      await api.post(`/seller/orders/${orderId}/return/pickup`);
      flash("Return picked up.");
      loadOrders();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };
  const completeReturn = async (orderId) => {
    if (!window.confirm("Confirm return received? This will refund the buyer's wallet.")) return;
    try {
      const { data } = await api.post(`/seller/orders/${orderId}/return/complete`, {});
      flash(`Return complete · refunded $${Number(data.refunded || 0).toFixed(2)}.`);
      loadOrders();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };

  const createStore = async (e) => {
    e.preventDefault();
    try {
      await api.post("/seller/store", storeForm);
      setStoreForm({ name: "", description: "", category_id: "" });
      flash("Store created!");
      loadAll();
    } catch (e2) { flash(formatApiError(e2?.response?.data?.detail), true); }
  };

  // === Bulk product table ===
  const updateDraft = (i, patch) => {
    setDrafts(d => d.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  };
  const addDraftRow = () => setDrafts(d => [...d, emptyDraft()]);
  const removeDraftRow = (i) => setDrafts(d => d.length > 1 ? d.filter((_, idx) => idx !== i) : [emptyDraft()]);
  const duplicateDraftRow = (i) => setDrafts(d => {
    const dup = { ...d[i], _uid: `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` };
    return [...d.slice(0, i + 1), dup, ...d.slice(i + 1)];
  });

  const buildProductPayload = (row) => {
    const sizes = (row.sizes || "").split(",").map(s => s.trim()).filter(Boolean);
    const stockNum = parseInt(row.stock || "10") || 10;
    const stock = sizes.length ? Object.fromEntries(sizes.map(s => [s, stockNum])) : { "One Size": stockNum };
    const pincodes = (row.pincodes || "").split(",").map(s => s.trim()).filter(Boolean);
    return {
      store_id: row.store_id,
      name: row.name,
      description: row.description || "",
      price: parseFloat(row.price || "0"),
      category_id: row.category_id,
      product_type_id: row.product_type_id || null,
      images: row.image ? [row.image] : [],
      video_url: row.video_url || null,
      banner_url: row.banner_url || null,
      sizes: sizes.length ? sizes : ["One Size"],
      stock,
      gender: row.gender || "unisex",
      delivery_days: parseInt(row.delivery_days || "5") || 5,
      pincodes: pincodes.length ? pincodes : ["*"],
      active: true,
      is_new_launch: !!row.is_new_launch,
      launch_date: row.launch_date || null,
      is_advance_booking: !!row.is_advance_booking,
      advance_booking_opens_at: row.advance_booking_opens_at || null,
      advance_booking_closes_at: row.advance_booking_closes_at || null,
      expected_ship_date: row.expected_ship_date || null,
    };
  };

  const submitDrafts = async () => {
    const valid = drafts.filter(r => r.name && r.price && r.category_id && r.store_id);
    if (!valid.length) { flash("Fill name, price, category and store on at least one row.", true); return; }
    try {
      const { data } = await api.post("/seller/products/bulk", {
        products: valid.map(buildProductPayload),
      });
      flash(`Added ${data.count} product${data.count === 1 ? "" : "s"}${data.errors?.length ? ` · ${data.errors.length} error(s)` : ""}.`);
      if (data.errors?.length) console.warn("Bulk errors:", data.errors);
      setDrafts([emptyDraft()]);
      loadAll();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };

  const onCsvFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text);
    if (rows.length < 2) { flash("CSV is empty.", true); return; }
    const header = rows[0].map(h => (h || "").trim().toLowerCase());
    const headerIdx = Object.fromEntries(CSV_FIELDS.map(f => [f, header.indexOf(f)]));
    const catByName = Object.fromEntries(categories.map(c => [c.name.toLowerCase(), c.category_id]));
    const storeByName = Object.fromEntries(stores.map(s => [s.name.toLowerCase(), s.store_id]));
    const typeByPair = {};  // "cat_id::name" → type_id
    productTypes.forEach(t => {
      typeByPair[`${t.category_id}::${t.name.toLowerCase()}`] = t.type_id;
    });
    const newDrafts = rows.slice(1).map(r => {
      const get = (k) => headerIdx[k] >= 0 ? (r[headerIdx[k]] || "").trim() : "";
      const cat_id = get("category_id") || catByName[get("category_name").toLowerCase()] || "";
      const store_id = get("store_id") || storeByName[get("store_name").toLowerCase()] || (stores[0]?.store_id || "");
      const type_id = get("product_type_id") ||
        typeByPair[`${cat_id}::${get("product_type_name").toLowerCase()}`] || "";
      return {
        _uid: `draft_csv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name: get("name"), description: get("description"),
        price: get("price"), category_id: cat_id, product_type_id: type_id, store_id,
        sizes: get("sizes"), stock: get("stock") || "10",
        image: get("image"), video_url: get("video_url"), banner_url: get("banner_url"),
        gender: get("gender") || "unisex", delivery_days: get("delivery_days") || "5",
        pincodes: get("pincodes"),
      };
    }).filter(r => r.name);
    if (!newDrafts.length) { flash("No valid rows in CSV.", true); return; }
    setDrafts(newDrafts);
    flash(`Loaded ${newDrafts.length} rows from CSV. Review and click Save all.`);
  };

  const downloadCsvTemplate = () => {
    const sample = [
      CSV_FIELDS.join(","),
      [
        "Sample Hoodie", "Cozy oversized hoodie", "49.99",
        "", "Fashion & Apparel", "", "Hoodie",
        "", stores[0]?.name || "Your store",
        "S,M,L", "10",
        "https://images.unsplash.com/photo-1556821840-3a9c6dcb952f?w=600",
        "", "", "unisex", "3", "*"
      ].join(","),
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "products_template.csv";
    a.click();
  };

  // === Manage table actions ===
  const startEdit = (p) => setEditing({
    product_id: p.product_id,
    name: p.name, price: p.price,
    category_id: p.category_id,
    product_type_id: p.product_type_id || "",
    gender: p.gender || "unisex", delivery_days: p.delivery_days || 5,
    image: p.images?.[0] || "", video_url: p.video_url || "", banner_url: p.banner_url || "",
    active: p.active !== false,
    is_new_launch: !!p.is_new_launch,
    launch_date: p.launch_date || "",
    is_advance_booking: !!p.is_advance_booking,
    advance_booking_opens_at: p.advance_booking_opens_at || "",
    advance_booking_closes_at: p.advance_booking_closes_at || "",
    expected_ship_date: p.expected_ship_date || "",
    wholesale_slabs: Array.isArray(p.wholesale_slabs) ? p.wholesale_slabs.map(s => ({ ...s })) : [],
    colors: Array.isArray(p.colors) ? p.colors.map(c => ({ ...c })) : [],
  });
  const saveEdit = async () => {
    try {
      await api.patch(`/seller/product/${editing.product_id}`, {
        name: editing.name,
        price: parseFloat(editing.price),
        category_id: editing.category_id,
        product_type_id: editing.product_type_id || null,
        gender: editing.gender,
        delivery_days: parseInt(editing.delivery_days || "5") || 5,
        images: editing.image ? [editing.image] : [],
        video_url: editing.video_url || null,
        banner_url: editing.banner_url || null,
        active: !!editing.active,
        is_new_launch: !!editing.is_new_launch,
        launch_date: editing.launch_date || null,
        is_advance_booking: !!editing.is_advance_booking,
        advance_booking_opens_at: editing.advance_booking_opens_at || null,
        advance_booking_closes_at: editing.advance_booking_closes_at || null,
        expected_ship_date: editing.expected_ship_date || null,
        wholesale_slabs: (editing.wholesale_slabs || []).length > 0
          ? editing.wholesale_slabs
          : null,
        colors: editing.colors || [],
      });
      setEditing(null);
      flash("Saved.");
      loadProductsTable();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };
  const deleteProduct = async (pid) => {
    if (!window.confirm("Delete this product permanently?")) return;
    try { await api.delete(`/seller/product/${pid}`); flash("Deleted."); loadProductsTable(); }
    catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };
  const toggleActive = async (p) => {
    try {
      await api.patch(`/seller/product/${p.product_id}`, { active: p.active === false ? true : false });
      flash(p.active === false ? "Reactivated." : "Deactivated.");
      loadProductsTable();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };
  const duplicateProduct = async (pid) => {
    try { await api.post(`/seller/product/${pid}/duplicate`); flash("Duplicated."); loadProductsTable(); }
    catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };

  // Flip between "not available" (all stock → 0) and "restock" (default 10
  // per size). Kept separate from `active` so sellers can distinguish
  // "hidden listing" vs "temporarily sold out but still visible".
  const toggleAvailability = async (p) => {
    try {
      const currentStock = p.stock || {};
      const sizes = Object.keys(currentStock).length ? Object.keys(currentStock) : (p.sizes || ["One Size"]);
      const isOOS = (p.total_stock ?? 0) === 0;
      const newStock = Object.fromEntries(sizes.map(s => [s, isOOS ? 10 : 0]));
      await api.patch(`/seller/product/${p.product_id}`, { stock: newStock });
      flash(isOOS ? "Restocked." : "Marked out of stock.");
      loadProductsTable();
      loadAll();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };

  // Shortcut — from the dashboard top-products row into the Manage tab
  // inline editor, without the seller having to search for the product.
  const editFromDashboard = (p) => {
    setEditing({
      product_id: p.product_id, name: p.name, price: p.price,
      category_id: p.category_id, product_type_id: p.product_type_id || "",
      gender: p.gender || "unisex",
      delivery_days: p.delivery_days || 5,
      image: p.images?.[0] || "", video_url: p.video_url || "", banner_url: p.banner_url || "",
      active: p.active !== false,
    });
    setTab("manage");
  };

  // === Profile save ===
  const saveProfile = async (payload) => {
    try {
      await api.put("/seller/profile", payload);
      flash("Profile updated.");
      loadProfile();
    } catch (e) { flash(formatApiError(e?.response?.data?.detail), true); }
  };

  if (!user || (user.role !== "seller" && user.role !== "admin")) {
    return <div className="max-w-xl mx-auto p-10 text-center">
      <div className="font-display text-2xl mb-4">Seller access required</div>
      <Link to="/register" className="btn-primary">Sign up as a seller</Link>
    </div>;
  }

  return (
    <SellerCurrencyProvider defaultCountry={analytics?.seller_country} scopeKey={user?.user_id || user?.email}>
    <div className="pb-24 md:pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      {/* Hero header — avatar + identity + plan + quick actions */}
      <div className="card relative overflow-hidden mb-5 grain" data-testid="seller-hero-header">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4FF00]/[0.07] via-transparent to-[#FF007F]/[0.07] pointer-events-none"/>
        <div className="relative p-5 sm:p-6 flex flex-col sm:flex-row gap-5">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {user.picture
              ? <img src={user.picture} alt="" className="w-16 h-16 rounded-2xl ring-2 ring-[#D4FF00]/40 object-cover shrink-0"/>
              : <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#D4FF00] to-[#FF007F] flex items-center justify-center text-black font-display font-black text-2xl shrink-0">{user.name?.[0]?.toUpperCase()}</div>}
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA]">Welcome back</div>
              <h1 className="font-display font-black text-2xl sm:text-4xl uppercase leading-tight truncate">{user.name}<span className="text-[#D4FF00]">.</span></h1>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <span className="text-xs text-[#A1A1AA]">@{(user.email||"").split("@")[0]}</span>
                {analytics && (
                  <>
                    <span className="text-[#52525B]">·</span>
                    <span className="text-xs text-[#A1A1AA]"><Store size={11} className="inline mr-1"/>{analytics.store_count || 0} stores</span>
                    <span className="text-[#52525B]">·</span>
                    <span className="text-xs text-[#A1A1AA]"><Package size={11} className="inline mr-1"/>{analytics.product_count || 0} products</span>
                  </>
                )}
                {subscription?.plan_name && (
                  <span className="chip text-[10px] active" data-testid="seller-plan-badge">
                    {subscription.plan_name}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-col gap-2 sm:items-end sm:shrink-0">
            <SellerCurrencyPicker/>
            <div className="grid grid-cols-3 sm:grid-cols-3 sm:flex sm:items-center gap-2 w-full">
            <Link to="/go-live" data-testid="qa-go-live"
              className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#FF3FA0] text-white text-xs font-bold uppercase tracking-wider active:scale-95 hover:shadow-[0_10px_28px_rgba(255,0,127,0.5)] transition shadow-[0_8px_24px_rgba(255,0,127,0.25)]">
              <Radio size={14}/> Go live
            </Link>
            <button onClick={() => setTab("manage")} data-testid="qa-products"
              className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 py-2.5 rounded-xl bg-[#D4FF00] text-black text-xs font-bold uppercase tracking-wider active:scale-95 hover:bg-[#D4FF00]/90 transition">
              <Plus size={14}/> Product
            </button>
            <Link to="/studio" data-testid="qa-studio"
              className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-br from-[#261534] via-[#261534] to-[#D4FF00]/10 border border-[#3D2845] text-[#FAFAFA] text-xs font-bold uppercase tracking-wider active:scale-95 hover:border-[#D4FF00] hover:shadow-[0_8px_24px_rgba(212,255,0,0.2)] transition">
              <VideoIcon size={14}/> Studio
            </Link>
            </div>
          </div>
        </div>
      </div>

      <SubscriptionBanner subscription={subscription}/>

      <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-6 lg:items-start">
        {/* Mobile / tablet — horizontal pill strip */}
        <div className="lg:hidden sticky top-[64px] z-30 -mx-4 px-4 bg-[#0F0817]/95 backdrop-blur border-b border-[#3D2845] mb-4 relative">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {TABS.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`seller-tab-${k}`}
                className={`shrink-0 px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition ${tab === k ? "border-[#D4FF00] text-[#D4FF00]" : "border-transparent text-[#A1A1AA] hover:text-[#FAFAFA]"}`}>{l}</button>
            ))}
          </div>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[#0F0817] to-transparent"/>
        </div>

        {/* Desktop — vertical left rail */}
        <aside className="hidden lg:block sticky top-[88px] self-start" data-testid="seller-sidebar">
          <nav className="flex flex-col gap-1 bg-[#261534] border border-[#3D2845] rounded-2xl p-2">
            {TABS.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`seller-tab-${k}`}
                className={`w-full text-left px-3 py-2 rounded-xl text-sm font-bold uppercase tracking-wider transition ${
                  tab === k
                    ? "bg-[#D4FF00]/10 text-[#D4FF00] border-l-2 border-[#D4FF00] pl-[10px]"
                    : "text-[#A1A1AA] hover:text-[#FAFAFA] hover:bg-[#3D2845]"
                }`}>
                {l}
              </button>
            ))}
          </nav>
        </aside>

        <div className="min-w-0">

      {msg && <div className="mb-4 text-sm text-[#D4FF00]" data-testid="seller-msg">{msg}</div>}
      {err && <div className="mb-4 text-sm text-[#FF007F]" data-testid="seller-err">{err}</div>}

      {tab === "dashboard" && (
        <>
          <SellerQuickLinks
            onTab={setTab}
            onShare={() => setShareStore(stores[0] || null)}
            hasStore={stores.length > 0}
          />
          <SellerDashboard analytics={analytics}
            onEdit={editFromDashboard}
            onToggleActive={toggleActive}
            onToggleAvailability={toggleAvailability}
            onDelete={deleteProduct}/>
        </>
      )}

      {tab === "coupons" && (
        <CouponsManager scope="seller" stores={stores} categories={categories}
          products={productsTable.length ? productsTable : []}/>
      )}

      {tab === "inbox" && (
        <SellerInboxTab/>
      )}

      {tab === "rentals" && (
        <SellerRentalsTab flash={flash}/>
      )}

      {tab === "kyc" && (
        <SellerKycWizard/>
      )}

      {tab === "payments" && (
        <SellerPaymentsTab stores={stores}/>
      )}

      {tab === "plans" && (
        <PlansTab audience="seller" subscription={subscription} flash={flash}/>
      )}

      {tab === "profile" && profile && (
        <ProfileTab profile={profile} categories={categories} onSave={saveProfile} />
      )}

      {tab === "orders" && (
        <SellerOrdersTab
          orders={orders}
          statusFilter={orderStatusFilter}
          setStatusFilter={setOrderStatusFilter}
          returnFilter={orderReturnFilter}
          setReturnFilter={setOrderReturnFilter}
          onUpdateStatus={updateOrderStatus}
          onConfirmPayment={confirmPayment}
          onDecideReturn={decideReturn}
          onMarkPickup={markReturnPickup}
          onComplete={completeReturn}
        />
      )}

      {tab === "stores" && (
        <div>
          <form onSubmit={createStore} className="card p-5 mb-6 grid gap-3">
            <div className="font-display font-bold uppercase text-sm">Create store</div>
            <input required placeholder="Store name" className="input-dark" value={storeForm.name} onChange={e => setStoreForm({ ...storeForm, name: e.target.value })} data-testid="store-name-input" />
            <textarea required placeholder="Description" className="input-dark" value={storeForm.description} onChange={e => setStoreForm({ ...storeForm, description: e.target.value })} data-testid="store-desc-input" />
            <select required className="input-dark" value={storeForm.category_id} onChange={e => setStoreForm({ ...storeForm, category_id: e.target.value })} data-testid="store-cat-select">
              <option value="">Select category</option>
              {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
            </select>
            <button className="btn-primary" type="submit" data-testid="store-create-btn">+ Create</button>
          </form>

          {/* Shipping countries — applies to all stores of this seller */}
          <ShippingCountriesCard stores={stores} onSaved={loadAll}/>

          <div className="grid md:grid-cols-2 gap-3">
            {stores.map(s => (
              <div key={s.store_id} className="card flex items-stretch overflow-hidden">
                <Link to={`/store/${s.store_id}`} className="flex flex-1 min-w-0" data-testid={`seller-store-link-${s.store_id}`}>
                  <img src={s.banner_url} className="w-24 h-24 object-cover shrink-0" alt="" />
                  <div className="p-3 flex-1 min-w-0">
                    <div className="font-bold truncate">{s.name}</div>
                    <div className="text-xs text-[#A1A1AA]">{s.followers} followers</div>
                  </div>
                </Link>
                <button
                  onClick={() => setShareStore(s)}
                  data-testid={`share-store-btn-${s.store_id}`}
                  title="Share this store with customers"
                  className="shrink-0 m-3 self-center flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#D4FF00] hover:text-[#D4FF00] transition text-xs font-bold uppercase tracking-wider"
                >
                  <Share2 size={14} /> Share
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {shareStore && (
        <ShareStoreModal store={shareStore} onClose={() => setShareStore(null)} />
      )}

      {tab === "manage" && (
        <ManageProductsTab
          stores={stores} categories={categories} productTypes={productTypes}
          tableSearch={tableSearch} setTableSearch={setTableSearch}
          tableStoreFilter={tableStoreFilter} setTableStoreFilter={setTableStoreFilter}
          tableCatFilter={tableCatFilter} setTableCatFilter={setTableCatFilter}
          tableSort={tableSort} setTableSort={setTableSort}
          productsTable={productsTable}
          editing={editing} setEditing={setEditing}
          onSaveEdit={saveEdit} onDelete={deleteProduct}
          onDuplicate={duplicateProduct} onToggleActive={toggleActive}
          onToggleAvailability={toggleAvailability}
          onAdd={() => setShowAddModal(true)}
        />
      )}

      {showAddModal && (
        <AddProductModal
          stores={stores}
          categories={categories}
          productTypes={productTypes}
          onClose={() => setShowAddModal(false)}
          onCreated={() => {
            setShowAddModal(false);
            flash("Product created.");
            loadProductsTable();
            loadAll();
          }}
        />
      )}

      {tab === "bulk" && (
        <BulkAddTab
          drafts={drafts}
          stores={stores} categories={categories} productTypes={productTypes}
          updateDraft={updateDraft}
          addRow={addDraftRow}
          removeRow={removeDraftRow}
          duplicateRow={duplicateDraftRow}
          onSubmit={submitDrafts}
          onCsvClick={() => csvInputRef.current?.click()}
          onCsvFile={onCsvFile}
          csvInputRef={csvInputRef}
          onTemplate={downloadCsvTemplate}
          onClear={() => setDrafts([emptyDraft()])}
        />
      )}

      {tab === "stream" && (
        <div className="card p-5 max-w-xl text-sm text-[#A1A1AA]" data-testid="go-live-redirect">
          Use the <Link to="/go-live" className="text-[#D4FF00] underline">Go Live studio</Link> to
          start a stream, apply effects, and pin products.
        </div>
      )}
        </div>
      </div>
    </div>
    </SellerCurrencyProvider>
  );
}

// ======================================================================
// Profile tab
// ======================================================================
function ProfileTab({ profile, categories, onSave }) {
  const locked = useMemo(() => new Set(profile.locked_fields || []), [profile]);
  const [form, setForm] = useState({
    name: profile.name || "",
    phone: profile.phone || "",
    phone_cc: profile.phone_cc || "",
    phone_country: profile.phone_country || "",
    address: profile.address || "",
    address_obj: profile.address_obj || {},
    id_proof_url: profile.id_proof_url || "",
    tax_id: profile.tax_id || "",
    bio: profile.bio || "",
    store_name: profile.store?.name || "",
    store_description: profile.store?.description || "",
    banner_url: profile.store?.banner_url || "",
    logo_url: profile.store?.logo_url || "",
  });

  const isLocked = (k) => locked.has(k);
  const Field = ({ k, label, type = "text", textarea = false, placeholder = "" }) => (
    <div className="grid gap-1">
      <label className="text-xs uppercase tracking-widest text-[#A1A1AA] font-bold flex items-center gap-1">
        {label}
        {isLocked(k) && <Lock size={10} className="text-[#FF007F]" data-testid={`profile-lock-${k}`} />}
      </label>
      {textarea ? (
        <textarea
          className={`input-dark min-h-[80px] ${isLocked(k) ? "opacity-50 cursor-not-allowed" : ""}`}
          value={form[k]} disabled={isLocked(k)} placeholder={placeholder}
          onChange={e => setForm({ ...form, [k]: e.target.value })}
          data-testid={`profile-${k}-input`}
        />
      ) : (
        <input
          type={type}
          className={`input-dark ${isLocked(k) ? "opacity-50 cursor-not-allowed" : ""}`}
          value={form[k]} disabled={isLocked(k)} placeholder={placeholder}
          onChange={e => setForm({ ...form, [k]: e.target.value })}
          data-testid={`profile-${k}-input`}
        />
      )}
    </div>
  );

  const onSubmit = (e) => {
    e.preventDefault();
    const payload = {};
    Object.entries(form).forEach(([k, v]) => { if (!isLocked(k)) payload[k] = v; });
    onSave(payload);
  };

  return (
    <form onSubmit={onSubmit} className="grid lg:grid-cols-3 gap-6">
      <div className="card p-5 lg:col-span-1 text-center">
        <img src={profile.picture} className="w-24 h-24 rounded-full mx-auto border-2 border-[#D4FF00]" alt="" />
        <div className="font-display font-black text-xl mt-3">{profile.name}</div>
        <div className="text-xs text-[#A1A1AA]">{profile.email}</div>
        <div className="chip active mt-2 inline-block">{profile.role}</div>
        {profile.store && (
          <div className="mt-4 pt-4 border-t border-[#3D2845]">
            <div className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Store</div>
            <img src={profile.store.banner_url} className="w-full h-20 object-cover rounded-lg mb-2" alt="" />
            <div className="font-bold text-sm">{profile.store.name}</div>
            <div className="text-xs text-[#A1A1AA]">{profile.store.followers || 0} followers · ⭐ {profile.store.rating || 5}</div>
          </div>
        )}
        {locked.size > 0 && (
          <div className="mt-4 p-3 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg text-left">
            <div className="text-xs font-bold uppercase tracking-widest text-[#FF007F] flex items-center gap-1 mb-1">
              <Lock size={10} /> Admin-locked fields
            </div>
            <div className="text-xs text-[#A1A1AA]">{[...locked].join(", ")}</div>
          </div>
        )}
      </div>

      <div className="card p-5 lg:col-span-2 grid gap-4">
        <div className="font-display font-bold uppercase text-sm flex items-center gap-2">
          <UserIcon size={14} /> Account details
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field k="name" label="Display name" />
          <Field k="tax_id" label="Tax ID / GSTIN / EIN" placeholder="99-9999999"/>
        </div>

        <PhoneField
          phone={form.phone} phoneCountry={form.phone_country}
          editable={!isLocked("phone") && !isLocked("phone_country")}
          onChange={(patch) => setForm({ ...form, ...patch })}
          testid="sellerprofile-phone"/>

        <div>
          <div className="text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-2 flex items-center gap-1">
            Address {isLocked("address_obj") && <Lock size={10} className="text-[#FF007F]"/>}
          </div>
          <AddressFields value={form.address_obj} editable={!isLocked("address_obj")}
            testid="sellerprofile-addr"
            onChange={(addr) => setForm({ ...form, address_obj: addr })}/>
        </div>

        <IdProofField value={form.id_proof_url} editable={!isLocked("id_proof_url")}
          testid="sellerprofile-id-proof"
          onChange={(v) => setForm({ ...form, id_proof_url: v })}/>

        <Field k="bio" label="Bio" textarea placeholder="Tell shoppers about you…" />

        <div className="font-display font-bold uppercase text-sm pt-4 border-t border-[#3D2845] flex items-center gap-2">
          <Store size={14} /> Storefront
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field k="store_name" label="Store name" />
          <Field k="logo_url" label="Logo URL" placeholder="https://…" />
        </div>
        <Field k="banner_url" label="Banner URL" placeholder="https://…" />
        <Field k="store_description" label="Store description" textarea />

        <div className="flex justify-end gap-2 pt-2">
          <button type="submit" className="btn-primary" data-testid="profile-save-btn">
            <Save size={14} className="inline mr-1" /> Save changes
          </button>
        </div>
      </div>
    </form>
  );
}

// ======================================================================
// Manage Products tab — table view with inline edit + actions
// ======================================================================
function ManageProductsTab({
  stores, categories, productTypes,
  tableSearch, setTableSearch, tableStoreFilter, setTableStoreFilter,
  tableCatFilter, setTableCatFilter, tableSort, setTableSort,
  productsTable, editing, setEditing,
  onSaveEdit, onDelete, onDuplicate, onToggleActive, onToggleAvailability, onAdd,
}) {
  const { fmt } = useSellerCurrency();
  return (
    <div className="space-y-3">
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] flex items-center gap-1.5">
          <div className="relative flex-1">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input placeholder="Search products by name…" className="input-dark pl-9 w-full"
              value={tableSearch} onChange={e => setTableSearch(e.target.value)} data-testid="seller-products-search" />
          </div>
          <VoiceMicButton
            testid="seller-products-search-voice"
            onTranscript={(t) => setTableSearch(t)}
            size={14} className="w-9 h-9"/>
        </div>
        <select className="input-dark sm:w-44" value={tableStoreFilter}
          onChange={e => setTableStoreFilter(e.target.value)} data-testid="seller-products-store">
          <option value="">All stores</option>
          {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.name}</option>)}
        </select>
        <select className="input-dark sm:w-44" value={tableCatFilter}
          onChange={e => setTableCatFilter(e.target.value)} data-testid="seller-products-cat">
          <option value="">All categories</option>
          {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
        </select>
        <select className="input-dark sm:w-44" value={tableSort}
          onChange={e => setTableSort(e.target.value)} data-testid="seller-products-sort">
          <option value="">Sort: Default</option>
          <option value="newest">Newest</option>
          <option value="sales">Best selling</option>
          <option value="price_asc">Price: low → high</option>
          <option value="price_desc">Price: high → low</option>
        </select>
        <button onClick={onAdd} className="btn-primary text-xs px-4 py-2" data-testid="seller-products-add">
          <Plus size={14} className="inline mr-1" />Add products
        </button>
      </div>

      <div className="card table-scroll" data-testid="seller-products-table-scroll">
        <table className="text-sm" style={{ minWidth: "1280px" }} data-testid="seller-products-table">
          <thead className="bg-[#3D2845] text-left sticky top-0">
            <tr>
              <Th>Product</Th><Th>Category</Th><Th>Store</Th>
              <Th right>Price</Th><Th right>Stock</Th><Th right>Sales</Th>
              <Th>Gender</Th><Th>Ship</Th><Th>Status</Th>
              <th className="p-3 text-xs uppercase tracking-widest font-bold text-[#A1A1AA] text-right sticky-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {productsTable.map(p => editing?.product_id === p.product_id ? (
              <React.Fragment key={p.product_id}>
              <tr className="border-t border-[#3D2845] bg-[#D4FF00]/5 editing-row">
                <td className="p-2">
                  <div className="flex items-center gap-2 min-w-[220px]">
                    <img src={editing.image || p.images?.[0]} className="w-10 h-10 rounded-lg object-cover shrink-0" alt="" />
                    <div className="flex-1 grid gap-1">
                      <input className="input-dark py-1" value={editing.name}
                        onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid={`edit-name-${p.product_id}`} />
                      <UrlPreview icon={ImageIcon} value={editing.image}
                        onChange={v => setEditing({ ...editing, image: v })}
                        placeholder="Image URL or upload"
                        accept="image/*"
                        testid={`edit-image-${p.product_id}`} />
                      <UrlPreview icon={VideoIcon} value={editing.video_url}
                        onChange={v => setEditing({ ...editing, video_url: v })}
                        placeholder="Video URL or upload"
                        accept="video/*"
                        testid={`edit-video-${p.product_id}`} />
                    </div>
                  </div>
                </td>
                <td className="p-2">
                  <select className="input-dark py-1" value={editing.category_id}
                    onChange={e => setEditing({ ...editing, category_id: e.target.value, product_type_id: "" })}>
                    {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                  </select>
                  <select className="input-dark py-1 mt-1 text-xs"
                    value={editing.product_type_id || ""}
                    data-testid={`edit-type-${p.product_id}`}
                    onChange={e => setEditing({ ...editing, product_type_id: e.target.value })}>
                    <option value="">— Type —</option>
                    {productTypes.filter(t => t.category_id === editing.category_id)
                      .map(t => <option key={t.type_id} value={t.type_id}>{t.name}</option>)}
                  </select>
                </td>
                <td className="p-2 text-xs">{p.store_name}</td>
                <td className="p-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <span className="text-[10px] text-[#A1A1AA] font-bold">USD</span>
                    <input type="number" step="0.01" className="input-dark py-1 w-20 text-right" value={editing.price}
                      onChange={e => setEditing({ ...editing, price: e.target.value })} data-testid={`edit-price-${p.product_id}`} />
                  </div>
                </td>
                <td className="p-2 text-right text-xs">{p.total_stock}</td>
                <td className="p-2 text-right text-xs">{p.sales || 0}</td>
                <td className="p-2">
                  <select className="input-dark py-1" value={editing.gender}
                    onChange={e => setEditing({ ...editing, gender: e.target.value })}>
                    <option value="unisex">Unisex</option><option value="men">Men</option>
                    <option value="women">Women</option><option value="kids">Kids</option>
                  </select>
                </td>
                <td className="p-2">
                  <input type="number" min={1} className="input-dark py-1 w-14" value={editing.delivery_days}
                    onChange={e => setEditing({ ...editing, delivery_days: e.target.value })} />
                </td>
                <td className="p-2">
                  <label className="text-xs flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!editing.active}
                      onChange={e => setEditing({ ...editing, active: e.target.checked })} />
                    Active
                  </label>
                </td>
                <td className="p-2 sticky-actions">
                  <div className="flex gap-1 justify-end">
                    <button onClick={onSaveEdit} title="Save" data-testid={`edit-save-${p.product_id}`}
                      className="w-7 h-7 rounded-lg bg-[#D4FF00] text-black flex items-center justify-center hover:opacity-90">
                      <Save size={12} />
                    </button>
                    <button onClick={() => setEditing(null)} title="Cancel"
                      className="w-7 h-7 rounded-lg bg-[#3D2845] flex items-center justify-center hover:bg-[#FF007F] hover:text-white">
                      <X size={12} />
                    </button>
                  </div>
                </td>
              </tr>
              <tr key={p.product_id + "_launch"} className="border-b border-[#3D2845] bg-[#D4FF00]/5 editing-row">
                <td colSpan={10} className="p-3">
                  <div className="grid sm:grid-cols-2 gap-4 text-xs">
                    {/* Launch */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer font-bold uppercase tracking-widest text-[10px]"
                        data-testid={`edit-launch-toggle-${p.product_id}`}>
                        <input type="checkbox" checked={!!editing.is_new_launch}
                          onChange={e => setEditing({ ...editing, is_new_launch: e.target.checked })}/>
                        🚀 Mark as New Launch
                      </label>
                      {editing.is_new_launch && (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-1">Launch date</div>
                          <input type="date" className="input-dark py-1 text-xs"
                            value={(editing.launch_date || "").slice(0, 10)}
                            onChange={e => setEditing({ ...editing, launch_date: e.target.value })}
                            data-testid={`edit-launch-date-${p.product_id}`}/>
                        </div>
                      )}
                    </div>
                    {/* Advance booking */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer font-bold uppercase tracking-widest text-[10px]"
                        data-testid={`edit-preorder-toggle-${p.product_id}`}>
                        <input type="checkbox" checked={!!editing.is_advance_booking}
                          onChange={e => setEditing({ ...editing, is_advance_booking: e.target.checked })}/>
                        📦 Allow Advance Booking (Pre-order)
                      </label>
                      {editing.is_advance_booking && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          <div>
                            <div className="text-[10px] uppercase font-bold tracking-widest text-[#A1A1AA] mb-1">Opens</div>
                            <input type="date" className="input-dark py-1 text-xs"
                              value={(editing.advance_booking_opens_at || "").slice(0, 10)}
                              onChange={e => setEditing({ ...editing, advance_booking_opens_at: e.target.value })}
                              data-testid={`edit-pre-opens-${p.product_id}`}/>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase font-bold tracking-widest text-[#A1A1AA] mb-1">Closes</div>
                            <input type="date" className="input-dark py-1 text-xs"
                              value={(editing.advance_booking_closes_at || "").slice(0, 10)}
                              onChange={e => setEditing({ ...editing, advance_booking_closes_at: e.target.value })}/>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase font-bold tracking-widest text-[#A1A1AA] mb-1">Ships on</div>
                            <input type="date" className="input-dark py-1 text-xs"
                              value={(editing.expected_ship_date || "").slice(0, 10)}
                              onChange={e => setEditing({ ...editing, expected_ship_date: e.target.value })}
                              data-testid={`edit-pre-ship-${p.product_id}`}/>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Wholesale slabs editor — spans the full edit row */}
                  <div className="mt-4 border-t border-[#3D2845] pt-3">
                    <WholesaleSlabsEditor
                      slabs={editing.wholesale_slabs || []}
                      onChange={(arr) => setEditing({ ...editing, wholesale_slabs: arr })}
                      compact
                    />
                  </div>
                  {/* Colour variants editor */}
                  <div className="mt-3">
                    <ColorChipEditor
                      colors={editing.colors || []}
                      onChange={(list) => setEditing({ ...editing, colors: list })}
                      testidPrefix={`edit-color-${p.product_id}`}
                      compact
                    />
                  </div>
                </td>
              </tr>
              </React.Fragment>
            ) : (
              <tr key={p.product_id} className="border-t border-[#3D2845] hover:bg-[#3D2845]/30" data-testid={`seller-row-${p.product_id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-2 min-w-[180px]">
                    <img src={p.images?.[0]} className="w-10 h-10 rounded-lg object-cover shrink-0" alt="" />
                    <div className="min-w-0">
                      <div className="font-bold text-xs line-clamp-1">{p.name}</div>
                      <div className="text-[10px] text-[#A1A1AA] font-mono-display">{p.product_id}</div>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {p.is_new_launch && (
                          <span className="text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#00F0FF]/15 text-[#00F0FF]"
                            title={p.launch_date ? `Launches ${p.launch_date.slice(0,10)}` : "New launch"}>
                            🚀 LAUNCH
                          </span>
                        )}
                        {p.is_advance_booking && (
                          <span className="text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#FF007F]/15 text-[#FF007F]"
                            title={p.expected_ship_date ? `Ships ${p.expected_ship_date.slice(0,10)}` : "Pre-order"}>
                            📦 PRE-ORDER
                          </span>
                        )}
                        {Array.isArray(p.wholesale_slabs) && p.wholesale_slabs.length > 0 && (
                          <span className="text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#D4FF00]/15 text-[#D4FF00]"
                            title={`Bulk discount: ${p.wholesale_slabs.map(s => `${s.min_qty}+ → ${s.discount_pct}%`).join(", ")}`}
                            data-testid={`seller-row-bulk-badge-${p.product_id}`}>
                            🏷 BULK
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex flex-col gap-1">
                    <span className="chip text-xs">{p.category_name}</span>
                    {p.product_type_id && (
                      <span className="text-[10px] text-[#00F0FF] font-bold uppercase tracking-wider">
                        {productTypes.find(t => t.type_id === p.product_type_id)?.name || "—"}
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3 text-xs">{p.store_name}</td>
                <td className="p-3 text-right font-display font-bold text-[#D4FF00]">{fmt(p.price)}</td>
                <td className="p-3 text-right text-xs">{p.total_stock}</td>
                <td className="p-3 text-right text-xs">{p.sales || 0}</td>
                <td className="p-3 text-xs capitalize">{p.gender || "—"}</td>
                <td className="p-3 text-xs">{p.delivery_days || 5}d</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded whitespace-nowrap ${
                    p.active === false ? "bg-[#A1A1AA]/15 text-[#A1A1AA]" :
                    (p.total_stock ?? 0) === 0 ? "bg-[#FF007F]/15 text-[#FF007F]" :
                    "bg-[#D4FF00]/15 text-[#D4FF00]"
                  }`}>
                    {p.active === false ? "Inactive" :
                      (p.total_stock ?? 0) === 0 ? "Not available" : "Active"}
                  </span>
                </td>
                <td className="p-3 sticky-actions">
                  <div className="flex gap-1 justify-end">
                    <Link to={`/product/${p.product_id}`} title="View"
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#00F0FF] hover:text-black flex items-center justify-center">
                      <SearchIcon size={12} />
                    </Link>
                    <button onClick={() => setEditing({
                      product_id: p.product_id, name: p.name, price: p.price,
                      category_id: p.category_id, product_type_id: p.product_type_id || "",
                      gender: p.gender || "unisex",
                      delivery_days: p.delivery_days || 5,
                      image: p.images?.[0] || "", video_url: p.video_url || "", banner_url: p.banner_url || "",
                      active: p.active !== false,
                      is_new_launch: !!p.is_new_launch,
                      launch_date: p.launch_date || "",
                      is_advance_booking: !!p.is_advance_booking,
                      advance_booking_opens_at: p.advance_booking_opens_at || "",
                      advance_booking_closes_at: p.advance_booking_closes_at || "",
                      expected_ship_date: p.expected_ship_date || "",
                    })}
                      data-testid={`seller-row-edit-${p.product_id}`}
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#D4FF00] hover:text-black flex items-center justify-center" title="Edit">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => onDuplicate(p.product_id)}
                      data-testid={`seller-row-dup-${p.product_id}`}
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#00F0FF] hover:text-black flex items-center justify-center" title="Duplicate">
                      <Copy size={12} />
                    </button>
                    <button onClick={() => onToggleActive(p)}
                      data-testid={`seller-row-toggle-${p.product_id}`}
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FFA500] hover:text-black flex items-center justify-center"
                      title={p.active === false ? "Reactivate" : "Deactivate"}>
                      {p.active === false ? <Power size={12} /> : <PowerOff size={12} />}
                    </button>
                    <button onClick={() => onToggleAvailability(p)}
                      data-testid={`seller-row-oos-${p.product_id}`}
                      className={`w-7 h-7 rounded-lg bg-[#3D2845] hover:text-black flex items-center justify-center ${
                        (p.total_stock ?? 0) === 0
                          ? "hover:bg-[#D4FF00] text-[#FF007F]"
                          : "hover:bg-[#FF007F] hover:text-white"
                      }`}
                      title={(p.total_stock ?? 0) === 0 ? "Restock (+10 per size)" : "Mark as not available (stock → 0)"}>
                      {(p.total_stock ?? 0) === 0 ? <PackageCheck size={12} /> : <PackageX size={12} />}
                    </button>
                    <button onClick={() => onDelete(p.product_id)}
                      data-testid={`seller-row-del-${p.product_id}`}
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center" title="Delete">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {productsTable.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-[#A1A1AA]">No products yet. Click "Add products" to create some.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-[#A1A1AA] text-right">{productsTable.length} products</div>
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`p-3 text-xs uppercase tracking-widest font-bold text-[#A1A1AA] ${right ? "text-right" : ""}`}>{children}</th>
);

// ======================================================================
// Bulk add tab — multi-row entry table + CSV upload
// ======================================================================
function BulkAddTab({
  drafts, stores, categories, productTypes,
  updateDraft, addRow, removeRow, duplicateRow,
  onSubmit, onCsvClick, onCsvFile, csvInputRef, onTemplate, onClear,
}) {
  return (
    <div className="space-y-4">
      <div className="card p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display font-bold uppercase text-sm">Bulk add products</div>
          <div className="text-xs text-[#A1A1AA] mt-1">Add many products at once. Add rows manually or import a CSV.</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={csvInputRef} type="file" accept=".csv,text/csv" className="hidden"
            onChange={e => { onCsvFile(e.target.files?.[0]); e.target.value = ""; }}
            data-testid="bulk-csv-input" />
          <button onClick={onTemplate} className="btn-secondary text-xs px-3 py-2" data-testid="bulk-csv-template">
            <FileSpreadsheet size={12} className="inline mr-1" /> CSV template
          </button>
          <button onClick={onCsvClick} className="btn-secondary text-xs px-3 py-2" data-testid="bulk-csv-upload">
            <Upload size={12} className="inline mr-1" /> Upload CSV
          </button>
          <button onClick={onClear} className="btn-secondary text-xs px-3 py-2" data-testid="bulk-clear">
            <X size={12} className="inline mr-1" /> Clear
          </button>
          <button onClick={addRow} className="btn-secondary text-xs px-3 py-2" data-testid="bulk-add-row">
            <Plus size={12} className="inline mr-1" /> Add row
          </button>
          <button onClick={onSubmit} className="btn-primary text-xs px-4 py-2" data-testid="bulk-save-all">
            <Save size={12} className="inline mr-1" /> Save all ({drafts.length})
          </button>
        </div>
      </div>

      <div className="card table-scroll" data-testid="bulk-table-scroll">
        <table className="text-sm" style={{ minWidth: "1600px" }} data-testid="bulk-table">
          <thead className="bg-[#3D2845] text-left">
            <tr>
              <Th>#</Th>
              <Th>Name *</Th>
              <Th>Store *</Th>
              <Th>Category *</Th>
              <Th>Product type</Th>
              <Th right>Price *</Th>
              <Th right>Stock</Th>
              <Th>Sizes</Th>
              <Th>Gender</Th>
              <Th right>Ship&nbsp;days</Th>
              <Th>Image URL</Th>
              <Th>Video URL</Th>
              <Th>Banner URL</Th>
              <Th>Pincodes</Th>
              <th className="p-3 text-xs uppercase tracking-widest font-bold text-[#A1A1AA] text-right sticky-actions"> </th>
            </tr>
          </thead>
          <tbody>
            {drafts.map((row, i) => (
              <tr key={row._uid || `bulk-${i}`} className="border-t border-[#3D2845] align-top" data-testid={`bulk-row-${i}`}>
                <td className="p-2 text-xs text-[#A1A1AA] font-mono-display">{i + 1}</td>
                <td className="p-2">
                  <input className="input-dark py-1 min-w-[160px]" placeholder="Hoodie / Sneakers / …"
                    value={row.name} onChange={e => updateDraft(i, { name: e.target.value })}
                    data-testid={`bulk-name-${i}`} />
                </td>
                <td className="p-2">
                  <select className="input-dark py-1 min-w-[140px]" value={row.store_id}
                    onChange={e => updateDraft(i, { store_id: e.target.value })} data-testid={`bulk-store-${i}`}>
                    <option value="">Select…</option>
                    {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.name}</option>)}
                  </select>
                </td>
                <td className="p-2">
                  <select className="input-dark py-1 min-w-[140px]" value={row.category_id}
                    onChange={e => updateDraft(i, { category_id: e.target.value, product_type_id: "" })} data-testid={`bulk-cat-${i}`}>
                    <option value="">Select…</option>
                    {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                  </select>
                </td>
                <td className="p-2">
                  <select className="input-dark py-1 min-w-[130px]" value={row.product_type_id || ""}
                    onChange={e => updateDraft(i, { product_type_id: e.target.value })}
                    data-testid={`bulk-type-${i}`}
                    disabled={!row.category_id}>
                    <option value="">— optional —</option>
                    {productTypes.filter(t => t.category_id === row.category_id)
                      .map(t => <option key={t.type_id} value={t.type_id}>{t.name}</option>)}
                  </select>
                </td>
                <td className="p-2">
                  <input type="number" step="0.01" className="input-dark py-1 w-20 text-right" placeholder="0.00"
                    value={row.price} onChange={e => updateDraft(i, { price: e.target.value })}
                    data-testid={`bulk-price-${i}`} />
                </td>
                <td className="p-2">
                  <input type="number" className="input-dark py-1 w-16 text-right" placeholder="10"
                    value={row.stock} onChange={e => updateDraft(i, { stock: e.target.value })} />
                </td>
                <td className="p-2">
                  <input className="input-dark py-1 w-28" placeholder="S,M,L"
                    value={row.sizes} onChange={e => updateDraft(i, { sizes: e.target.value })} />
                </td>
                <td className="p-2">
                  <select className="input-dark py-1" value={row.gender}
                    onChange={e => updateDraft(i, { gender: e.target.value })}>
                    <option value="unisex">Unisex</option>
                    <option value="men">Men</option>
                    <option value="women">Women</option>
                    <option value="kids">Kids</option>
                  </select>
                </td>
                <td className="p-2">
                  <input type="number" min={1} className="input-dark py-1 w-14 text-right"
                    value={row.delivery_days} onChange={e => updateDraft(i, { delivery_days: e.target.value })} />
                </td>
                <td className="p-2">
                  <UrlPreview icon={ImageIcon} value={row.image}
                    onChange={v => updateDraft(i, { image: v })} placeholder="https://image.jpg"
                    accept="image/*"
                    testid={`bulk-image-${i}`} />
                </td>
                <td className="p-2">
                  <UrlPreview icon={VideoIcon} value={row.video_url}
                    onChange={v => updateDraft(i, { video_url: v })} placeholder="https://video.mp4"
                    accept="video/*"
                    testid={`bulk-video-${i}`} />
                </td>
                <td className="p-2">
                  <UrlPreview icon={ImageIcon} value={row.banner_url}
                    onChange={v => updateDraft(i, { banner_url: v })} placeholder="https://banner.jpg"
                    accept="image/*"
                    testid={`bulk-banner-${i}`} />
                </td>
                <td className="p-2">
                  <input className="input-dark py-1 w-28" placeholder="* or 10001,94016"
                    value={row.pincodes || ""} onChange={e => updateDraft(i, { pincodes: e.target.value })} />
                </td>
                <td className="p-2 text-right sticky-actions">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => duplicateRow(i)} title="Duplicate"
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#00F0FF] hover:text-black flex items-center justify-center"
                      data-testid={`bulk-dup-${i}`}>
                      <Copy size={12} />
                    </button>
                    <button onClick={() => removeRow(i)} title="Remove row"
                      className="w-7 h-7 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center"
                      data-testid={`bulk-remove-${i}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-[#A1A1AA]">
        Tip: download the CSV template to see all available columns. Required: name, price, store, category.
      </div>
    </div>
  );
}

function UrlPreview({ icon: Icon, value, onChange, placeholder, testid, accept }) {
  // Accepts either a pasted URL or a direct file upload. File is read as a
  // data URL and stored inline — same pattern as the KYC wizard.
  const inputRef = useRef(null);
  const isVideo = (accept || "").includes("video");
  const MAX_BYTES = isVideo ? 10 * 1024 * 1024 : 2.5 * 1024 * 1024;

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) {
      alert(`File too large. Max ${isVideo ? "10 MB (video)" : "2.5 MB (image)"}.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const looksLikeImg = value && !value.startsWith("data:video");
  return (
    <div className="flex items-center gap-1 min-w-[170px]">
      <input ref={inputRef} type="file" accept={accept || "image/*"}
        className="hidden" onChange={onPick} data-testid={`${testid}-file`}/>
      {value ? (
        isVideo || value.startsWith("data:video") ? (
          <video src={value} className="w-7 h-7 rounded object-cover bg-[#3D2845] shrink-0" muted/>
        ) : looksLikeImg ? (
          <img src={value} alt="" className="w-7 h-7 rounded object-cover bg-[#3D2845] shrink-0"
            onError={(e) => { e.currentTarget.style.opacity = 0.3; }}/>
        ) : (
          <div className="w-7 h-7 rounded bg-[#3D2845] flex items-center justify-center shrink-0">
            <Icon size={12} className="text-[#A1A1AA]"/>
          </div>
        )
      ) : (
        <div className="w-7 h-7 rounded bg-[#3D2845] flex items-center justify-center shrink-0">
          <Icon size={12} className="text-[#A1A1AA]"/>
        </div>
      )}
      <input className="input-dark py-1 text-xs flex-1 min-w-0" placeholder={placeholder}
        value={value && value.startsWith("data:") ? "" : (value || "")}
        onChange={e => onChange(e.target.value)} data-testid={testid}/>
      <button type="button" onClick={() => inputRef.current?.click()}
        title={isVideo ? "Upload video" : "Upload image"}
        data-testid={`${testid}-upload-btn`}
        className="w-7 h-7 shrink-0 rounded bg-[#3D2845] hover:bg-[#D4FF00] hover:text-black flex items-center justify-center">
        <Upload size={12}/>
      </button>
    </div>
  );
}


// ======================================================================
// Orders tab — fulfillment + return management
// ======================================================================
// Status constants + OrdersTab + ShippingCountriesCard + SellerOrderCard +
// SellerInboxTab moved to /components/seller/ in iter-41 component-split.



// ──────────────────────────────────────────────────────────────────────
// Subscription status banner (pending approval / suspended / expired / active)
// ──────────────────────────────────────────────────────────────────────
function SubscriptionBanner({ subscription }) {
  if (!subscription) return null;
  const { ok, reason, seller_status, subscription_expires_at } = subscription;
  if (ok) {
    if (!subscription_expires_at) return null;
    const exp = new Date(subscription_expires_at);
    const daysLeft = Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft > 14) return null;
    return (
      <div className="card p-4 mb-4 border-[#FF9B00] bg-[#FF9B00]/10 flex items-center gap-3"
        data-testid="subscription-banner-warn">
        <Clock size={20} className="text-[#FF9B00] shrink-0"/>
        <div className="flex-1 text-sm">
          <div className="font-bold text-[#FF9B00]">Subscription expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}</div>
          <div className="text-xs text-[#A1A1AA]">Contact admin to renew before {exp.toLocaleDateString()} to avoid interruption.</div>
        </div>
      </div>
    );
  }
  const messages = {
    pending_approval: { title: "Pending admin approval",
      body: "Your seller account is under review. You can browse the dashboard, but creating stores, products, or going live is paused until approval.",
      color: "#00F0FF" },
    rejected: { title: "Application rejected",
      body: "Your seller application has been rejected. Please contact support if you believe this is a mistake.",
      color: "#FF007F" },
    suspended: { title: "Account suspended",
      body: "Your seller account is suspended. Please contact support to resolve this.",
      color: "#FF007F" },
    subscription_expired: { title: "Subscription expired",
      body: "Renew your subscription to resume listings, going live, and all seller actions.",
      color: "#FF9B00" },
  };
  const m = messages[reason] || { title: seller_status || "Inactive",
    body: "Your seller account is inactive.", color: "#FF007F" };
  return (
    <div className="card p-4 mb-4 flex items-center gap-3"
      style={{ borderColor: m.color, background: `${m.color}14` }}
      data-testid="subscription-banner-blocked">
      <Lock size={20} style={{ color: m.color }} className="shrink-0"/>
      <div className="flex-1 text-sm">
        <div className="font-bold" style={{ color: m.color }}>{m.title}</div>
        <div className="text-xs text-[#A1A1AA]">{m.body}</div>
      </div>
    </div>
  );
}
